import React, { useState, useEffect, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { organizationApi, Organization } from '@/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

const OrganizationSwitcher: React.FC = () => {
  const { user, activeOrganization, setActiveOrganization } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const userOrgs = user?.organizations || [];

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (userOrgs.length === 0) {
        setOrganizations([]);
        setLoading(false);
        return;
      }

      try {
        const ids = userOrgs
          .map(org => org.organizationId)
          .filter(id => id && id.trim() !== '');
        const response = await organizationApi.getByIds(ids);
        if (response.data?.success) {
          setOrganizations(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [userOrgs]);

  const selectedOrg = organizations.find(
    org => org._id === activeOrganization?.organizationId
  ) || organizations[0];

  const handleChange = async (org: Organization) => {
    const userOrg = userOrgs.find(o => o.organizationId === org._id);
    if (!userOrg) return;

    try {
      await setActiveOrganization(userOrg);
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Failed to switch organization:', error);
    }
  };

  const handleCreateNew = () => {
    navigate('/app/organization?tab=new-organization');
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <LoadingSpinner size="sm" />
        <span className="text-sm text-gray-500">Loading orgs...</span>
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <button
        onClick={handleCreateNew}
        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors w-full"
      >
        <PlusIcon className="h-5 w-5 mr-2" />
        Create Organization
      </button>
    );
  }

  return (
    <div className="w-full">
      <Listbox value={selectedOrg} onChange={handleChange}>
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white dark:bg-gray-800 py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors sm:text-sm">
            <span className="flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" aria-hidden="true" />
              <span className="block truncate text-gray-900 dark:text-white">
                {selectedOrg?.name || 'Select Organization'}
              </span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {organizations.map((org) => {
                const userOrg = userOrgs.find(o => o.organizationId === org._id);
                return (
                  <Listbox.Option
                    key={org._id}
                    value={org}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active ? 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100' : 'text-gray-900 dark:text-gray-100'
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className="flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" aria-hidden="true" />
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {org.name}
                          </span>
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {org.country} • {userOrg?.role || 'Member'}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600 dark:text-amber-400">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                );
              })}
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              <button
                onClick={handleCreateNew}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create New Organization
              </button>
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};

export default OrganizationSwitcher;
